// =============================================
// ZonesTab — "Domeny w CF": zony ze WSZYSTKICH kont panelu w jednej tabeli.
// Kolumny: domena · konto · status · NS (kopiuj) · A @ (leniwie: "Sprawdz
// rekordy" -> listDnsRecords sekwencyjnie per zona) · akcje (ZoneRowActions,
// dochodza w kolejnym commicie).
// Dane z useAllZones (cache w CloudflarePage, przezywa zmiane zakladki).
// =============================================
import { useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw, AlertTriangle, Search, ListChecks } from "lucide-react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { AllZonesState } from "@/hooks/useAllZones";
import { listDnsRecords, toFqdn, type CfZoneWithAccount } from "@/services/cloudflareService";
import { StatusBadge } from "@/components/cloudflare/shared/StatusBadge";
import { NsList } from "@/components/cloudflare/shared/NsList";

export interface ZoneRecordsInfo {
  aRoot: string | null;
  aWww: string | null;
  error?: string;
}

interface Props {
  accounts: CloudflareAccount[];
  loading: boolean;
  allZones: AllZonesState;
  /** Akcje inline per wiersz; setRecord pozwala odswiezyc kolumne A po "Podepnij hosting". */
  renderActions?: (zone: CfZoneWithAccount, records: ZoneRecordsInfo | undefined, setRecord: (zoneId: string, info: ZoneRecordsInfo) => void) => ReactNode;
}

const fmtTime = (ts: number | null) => (ts ? new Date(ts).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" }) : "");

export const ZonesTab = ({ accounts, loading, allZones, renderActions }: Props) => {
  const { zones, failures, loading: zonesLoading, loadedAt, refresh } = allZones;
  const [search, setSearch] = useState("");
  const [records, setRecords] = useState<Record<string, ZoneRecordsInfo>>({});
  const [checking, setChecking] = useState<{ done: number; total: number } | null>(null);

  const tokenFor = (cfAccountId: string) => accounts.find((a) => a.id === cfAccountId)?.api_token;
  const setRecord = (zoneId: string, info: ZoneRecordsInfo) => setRecords((prev) => ({ ...prev, [zoneId]: info }));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? zones.filter((z) => z.name.toLowerCase().includes(q) || z.cfAccountLabel.toLowerCase().includes(q)) : zones;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [zones, search]);

  /** Sekwencyjnie (zona po zonie) — bez rownoleglosci, zeby nie lapac 429. */
  const checkRecords = async () => {
    const targets = visible.filter((z) => !records[z.id]);
    if (!targets.length) return;
    setChecking({ done: 0, total: targets.length });
    let done = 0;
    for (const z of targets) {
      const token = tokenFor(z.cfAccountId);
      let info: ZoneRecordsInfo;
      if (!token) info = { aRoot: null, aWww: null, error: "brak tokena konta" };
      else {
        try {
          const recs = await listDnsRecords(token, z.id);
          const root = toFqdn("@", z.name);
          const www = toFqdn("www", z.name);
          info = {
            aRoot: recs.find((r) => r.type === "A" && r.name.toLowerCase() === root)?.content ?? null,
            aWww: recs.find((r) => r.type === "A" && r.name.toLowerCase() === www)?.content ?? null,
          };
        } catch (e) {
          info = { aRoot: null, aWww: null, error: e instanceof Error ? e.message : String(e) };
        }
      }
      setRecords((prev) => ({ ...prev, [z.id]: info }));
      done++;
      setChecking({ done, total: targets.length });
    }
    setChecking(null);
  };

  if (!loading && accounts.length === 0) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">Najpierw dodaj konto w zakładce <strong>Konta</strong>.</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => void refresh()} disabled={zonesLoading || loading} variant="outline">
          {zonesLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {loadedAt ? "Odśwież domeny" : "Pobierz domeny"}
        </Button>
        {loadedAt && (
          <span className="text-xs text-muted-foreground">
            {zones.length} {zones.length === 1 ? "zona" : "zon"} z {accounts.length} {accounts.length === 1 ? "konta" : "kont"} · {fmtTime(loadedAt)}
          </span>
        )}
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj domeny / konta…" className="pl-8 w-64" />
        </div>
        <Button variant="outline" onClick={() => void checkRecords()} disabled={!loadedAt || !!checking || visible.every((z) => records[z.id])} title="Pobiera rekordy A dla @ i www — zona po zonie">
          {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListChecks className="h-4 w-4 mr-2" />}
          {checking ? `Sprawdzam ${checking.done}/${checking.total}…` : "Sprawdź rekordy"}
        </Button>
      </div>

      {failures.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Nie udało się pobrać zon z {failures.length} {failures.length === 1 ? "konta" : "kont"}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 text-xs space-y-0.5">
              {failures.map((f) => <li key={f.cfAccountId}><strong>{f.label}</strong>: {f.error}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {!loadedAt ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground text-sm">Kliknij „Pobierz domeny”, żeby zobaczyć zony ze wszystkich kont.</CardContent></Card>
      ) : visible.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">{zones.length ? "Nic nie pasuje do wyszukiwania." : "Brak zon na kontach."}</CardContent></Card>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domena</TableHead>
                <TableHead>Konto</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Nameservery</TableHead>
                <TableHead>A @ / www</TableHead>
                {renderActions && <TableHead className="text-right">Akcje</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((z) => {
                const rec = records[z.id];
                return (
                  <TableRow key={z.id}>
                    <TableCell className="font-mono text-sm">{z.name}</TableCell>
                    <TableCell className="text-sm">{z.cfAccountLabel}</TableCell>
                    <TableCell><StatusBadge status={z.status} /></TableCell>
                    <TableCell className="max-w-[320px]"><NsList ns={z.name_servers} compact /></TableCell>
                    <TableCell className="text-xs font-mono">
                      {!rec ? <span className="text-muted-foreground">—</span>
                        : rec.error ? <span className="text-red-400" title={rec.error}>błąd</span>
                          : (
                            <span className={rec.aRoot ? "" : "text-amber-400"}>
                              {rec.aRoot ?? "brak"}{rec.aWww && rec.aWww !== rec.aRoot ? ` / ${rec.aWww}` : rec.aWww ? "" : " / brak"}
                            </span>
                          )}
                    </TableCell>
                    {renderActions && <TableCell className="text-right">{renderActions(z, rec, setRecord)}</TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
